import type { CodingTier, TaskCategory } from './types.js';

export interface CloudReference {
  id: string;
  displayName: string;
  provider: 'anthropic' | 'openai' | 'google' | 'xai' | 'other';
  baseQualityScore: number;
  codingTier: CodingTier;
  approxTps: number;
  approxLatencyMs: number;
  costPerMTokIn: number;
  costPerMTokOut: number;
  notes: string;
}

export const CLOUD_REFERENCES: [CloudReference, ...CloudReference[]] = [
  {
    id: 'claude-opus-4-7',
    displayName: 'Claude Opus 4.7 (cloud)',
    provider: 'anthropic',
    baseQualityScore: 95,
    codingTier: 'S',
    approxTps: 60,
    approxLatencyMs: 900,
    costPerMTokIn: 15,
    costPerMTokOut: 75,
    notes: 'State-of-the-art on long-horizon agent work.',
  },
  {
    id: 'claude-sonnet-4-6',
    displayName: 'Claude Sonnet 4.6 (cloud)',
    provider: 'anthropic',
    baseQualityScore: 91,
    codingTier: 'S',
    approxTps: 90,
    approxLatencyMs: 650,
    costPerMTokIn: 3,
    costPerMTokOut: 15,
    notes: 'Default coding workhorse. Best $/quality ratio in cloud.',
  },
  {
    id: 'gpt-5-pro',
    displayName: 'GPT-5 Pro (cloud)',
    provider: 'openai',
    baseQualityScore: 93,
    codingTier: 'S',
    approxTps: 70,
    approxLatencyMs: 800,
    costPerMTokIn: 10,
    costPerMTokOut: 40,
    notes: 'Strong reasoning, slower first-token.',
  },
  {
    id: 'gpt-4o-mini',
    displayName: 'GPT-4o Mini (cloud)',
    provider: 'openai',
    baseQualityScore: 78,
    codingTier: 'B',
    approxTps: 140,
    approxLatencyMs: 450,
    costPerMTokIn: 0.15,
    costPerMTokOut: 0.6,
    notes: 'Fast, cheap. Fine for lightweight tool calls.',
  },
  {
    id: 'gemini-2.5-pro',
    displayName: 'Gemini 2.5 Pro (cloud)',
    provider: 'google',
    baseQualityScore: 89,
    codingTier: 'A',
    approxTps: 85,
    approxLatencyMs: 700,
    costPerMTokIn: 1.25,
    costPerMTokOut: 5,
    notes: '1M-token context, very strong on large codebases.',
  },
];

export function findCloud(id: string): CloudReference | undefined {
  return CLOUD_REFERENCES.find((c) => c.id === id);
}

export function cloudQualityForTask(ref: CloudReference, task: TaskCategory): number {
  const taskFactor: Record<TaskCategory, number> = {
    coding: 1.0,
    review: 0.98,
    debugging: 0.96,
    reasoning: 1.02,
    docs: 1.0,
    'tool-use': 0.95,
  };
  return Math.min(100, ref.baseQualityScore * (taskFactor[task] ?? 1));
}

export function estimateCloudCost(ref: CloudReference, inputTokens: number, outputTokens: number): number {
  return (ref.costPerMTokIn * inputTokens) / 1_000_000 + (ref.costPerMTokOut * outputTokens) / 1_000_000;
}
